import { CombinedGraphQLErrors } from '@apollo/client';
import { getClient } from '@api/client';
import { graphql } from '@api/generated';
import { MutationCompleteOutboundLeadTaskArgs } from '@api/generated/graphql';

/**
 * BFF-defined error extension codes returned from `completeOutboundLeadTask`.
 * Mirror of the BFF's `error.extensions.code` values — keep this enum in sync
 * with the BFF contract (sub-task 1.2).
 */
export enum CompleteOutboundLeadTaskErrorCode {
  OutcomeRequiresJobId = 'OUTCOME_REQUIRES_JOB_ID',
}

/**
 * Domain error thrown by `completeTask`. Decouples callers from the Apollo /
 * GraphQL transport shape: callers branch on `err instanceof
 * CompleteOutboundLeadTaskError` and inspect the typed `code` field instead
 * of digging through `errors[0].extensions.code`. The original error is
 * preserved via the standard `cause` chain for diagnostics.
 */
export class CompleteOutboundLeadTaskError extends Error {
  override readonly name = 'CompleteOutboundLeadTaskError';
  readonly code?: CompleteOutboundLeadTaskErrorCode;

  constructor(
    message: string,
    options: {
      code?: CompleteOutboundLeadTaskErrorCode;
      cause?: unknown;
    } = {},
  ) {
    super(message, options.cause !== undefined ? { cause: options.cause } : {});
    this.code = options.code;
  }
}

export const COMPLETE_OUTBOUND_LEAD_TASK_MUTATION = graphql(`
  mutation CompleteOutboundLeadTask(
    $taskId: ID!
    $tenantId: ID!
    $input: TaskCompleteInput!
  ) {
    completeOutboundLeadTask(
      taskId: $taskId
      tenantId: $tenantId
      input: $input
    )
  }
`);

const KNOWN_ERROR_CODES = new Set<string>(
  Object.values(CompleteOutboundLeadTaskErrorCode),
);

const extractKnownCode = (
  err: unknown,
): CompleteOutboundLeadTaskErrorCode | undefined => {
  if (!CombinedGraphQLErrors.is(err)) return undefined;
  const code = err.errors[0]?.extensions?.['code'];
  return typeof code === 'string' && KNOWN_ERROR_CODES.has(code)
    ? (code as CompleteOutboundLeadTaskErrorCode)
    : undefined;
};

/**
 * Completes an STL outbound lead task with the supplied outcome and notes.
 *
 * For Booked outcomes (and when the BFF requires it), callers should populate
 * `input.jobBooking = { channelId, jobId }` so the BFF can attach a
 * JobBooking activity to the interaction before completing the task in Task
 * Hub. The `channelId` is the PCS conversation sid captured on
 * `phone.pcs.conversation.fetched`.
 *
 * Throws `CompleteOutboundLeadTaskError` on failure. Known BFF validation
 * codes are surfaced as `err.code`; all other failures (including transport
 * errors and falsy mutation results) carry an undefined `code` and the
 * original error in `err.cause`.
 */
export const completeTask = async (
  variables: MutationCompleteOutboundLeadTaskArgs,
): Promise<boolean> => {
  try {
    const { data } = await getClient().mutate({
      mutation: COMPLETE_OUTBOUND_LEAD_TASK_MUTATION,
      variables,
    });

    if (!data?.completeOutboundLeadTask) {
      throw new CompleteOutboundLeadTaskError(
        'Failed to complete speed to lead task',
      );
    }

    return data.completeOutboundLeadTask;
  } catch (err) {
    if (err instanceof CompleteOutboundLeadTaskError) throw err;
    throw new CompleteOutboundLeadTaskError(
      err instanceof Error
        ? err.message
        : 'Failed to complete speed to lead task',
      { code: extractKnownCode(err), cause: err },
    );
  }
};
